import type { NudgeWhere } from "@tillix/db/validators/NudgeWhere";
import { computed } from "mobx";
import { ExtendedModel, modelAction, tProp, types } from "mobx-keystone";
import { api } from "../../../orpc/client";
import { createTStore, type TStoreInit } from "../../lib/models/create-t-store";
import { TStore } from "../../lib/models/t-store";
import { NudgeModel, NudgePreferencesModel } from "./model.nudge";
import { nudgeNS } from "./ns.nudge";

type NudgeWhereInput = typeof NudgeWhere.infer;

@nudgeNS.store
export class NudgeStore extends ExtendedModel(
  TStore<NudgeModel, NudgeWhereInput, typeof api.notification>,
  {
    allPreferences: tProp(types.array(NudgePreferencesModel), () => []),
  },
) {
  Model = NudgeModel;
  api = api.notification;

  @computed
  get unreadNudges(): NudgeModel[] {
    return this.data.filter((nudge) => !nudge.read);
  }

  @computed
  get unreadCount(): number {
    return this.unreadNudges.length;
  }

  /**
   * @deprecated Use `unreadNudges` instead.
   */
  @computed
  get nudges(): NudgeModel[] {
    return this.unreadNudges;
  }

  @modelAction
  nudgeById(id: string): NudgeModel | undefined {
    return this.data.find((nudge) => nudge.id === id);
  }

  @modelAction
  private setRead(id: string, read: boolean) {
    const model = this.data.find((nudge) => nudge.id === id);
    if (model) {
      model.read = read;
    }
  }

  async markAsRead(id: string, read = true) {
    const result = await api.notification.put(
      { id, read },
      { context: { disableCache: true } },
    );

    this.setRead(id, read);

    return result;
  }

  preferencesForAccount(
    serviceAccountId: string,
  ): NudgePreferencesModel | undefined {
    return this.allPreferences.find(
      (p) => p.serviceAccountId === serviceAccountId,
    );
  }

  async getPreferences(
    options: Parameters<typeof api.notification.preferences.get>[0],
  ) {
    return api.notification.preferences.get(options, {
      context: { disableCache: true },
    });
  }

  async savePreferences(
    options: Parameters<typeof api.notification.preferences.put>[0],
  ) {
    await api.notification.preferences.put(options, {
      context: { disableCache: true },
    });
  }
}

export const createNotificationStore = (
  incomingData?: TStoreInit<NudgeModel, NudgeStore>,
) => createTStore<NudgeModel, NudgeStore>(NudgeStore, incomingData);
